/**
 * Weather Widget Component
 * Displays current weather with fancy graphics
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiDroplet, FiWind } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const WeatherWidget = ({ location }) => {
  const { isAdmin } = useAuth();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = location ? { city: location } : {};
        console.log('[WeatherWidget] Fetching weather from:', `/weather/current`, params);
        const response = await api.get('/weather/current', { params });
        console.log('[WeatherWidget] Weather response:', response.data);
        if (response.data.success) {
          setWeather(response.data.data);
          console.log('[WeatherWidget] Weather data set successfully');
        } else {
          console.error('[WeatherWidget] API returned success:false', response.data);
          setError(response.data.message || 'Failed to fetch weather');
        }
      } catch (err) {
        console.error('[WeatherWidget] Weather fetch error:', err);
        console.error('[WeatherWidget] Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        setError(err.response?.data?.message || err.message || 'Unable to load weather');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    // Refresh every 10 minutes
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, [location]);

  if (loading) {
    console.log('[WeatherWidget] Rendering loading state');
    return (
      <WeatherCard>
        <LoadingSpinner />
        <LoadingText>Loading weather...</LoadingText>
      </WeatherCard>
    );
  }

  if (error) {
    console.log('[WeatherWidget] Error state - isAdmin:', isAdmin, 'error:', error);
    // Only show error message if user is an administrator
    if (isAdmin) {
      return (
        <WeatherCard>
          <ErrorMessage>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorText>Weather unavailable</ErrorText>
            <ErrorSubtext>{error}</ErrorSubtext>
          </ErrorMessage>
        </WeatherCard>
      );
    }
    // Hide silently for non-admin users
    console.log('[WeatherWidget] Hiding error (user is not admin)');
    return null;
  }

  if (!weather) {
    console.log('[WeatherWidget] No weather data, hiding widget');
    return null; // Only hide if no data and no error
  }

  console.log('[WeatherWidget] Rendering weather widget with data:', weather);

  // Get weather icon URL from OpenWeatherMap
  const iconUrl = `https://openweathermap.org/img/wn/${weather.icon}@2x.png`;

  return (
    <WeatherCard
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HorizontalLayout>
        <LeftSection>
          <WeatherIcon src={iconUrl} alt={weather.description} />
          <Temperature>{weather.temperature}{weather.units.temperature}</Temperature>
        </LeftSection>
        <RightSection>
          <LocationName>{weather.location}</LocationName>
          <Description>{weather.description}</Description>
          <QuickStats>
            <StatItem>
              <FiDroplet size={12} />
              <span>{weather.humidity}%</span>
            </StatItem>
            <StatItem>
              <FiWind size={12} />
              <span>{weather.windSpeed} {weather.units.speed}</span>
            </StatItem>
          </QuickStats>
        </RightSection>
      </HorizontalLayout>
    </WeatherCard>
  );
};

const WeatherCard = styled.div`
  background: transparent;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: white;
  box-shadow: none;
  position: relative;
  overflow: visible;
  width: 100%;
  max-width: 240px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
`;

const HorizontalLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  z-index: 1;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const RightSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const WeatherIcon = styled.img`
  width: 36px;
  height: 36px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  margin-bottom: 0.25rem;
`;

const Temperature = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const LocationName = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  opacity: 0.95;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Description = styled.div`
  font-size: 0.65rem;
  text-transform: capitalize;
  opacity: 0.9;
  margin-bottom: 0.375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuickStats = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  opacity: 0.9;
  
  svg {
    flex-shrink: 0;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

const ErrorText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ErrorSubtext = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

export default WeatherWidget;

